import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeekendKursExternalPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [numDays, setNumDays] = useState(2);
  const [availablePeriods, setAvailablePeriods] = useState([]);
  const [formData, setFormData] = useState({
    stable_name: '',
    stable_address: '',
    selected_date_period: '',
    day_preference: 'first',
    alternative_date_period: '',
    alternative_day_preference: 'first',
    course_type: 'single',
    additional_notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(true);

  const INSTRUCTOR_ID = 'julie-weekend-external';
  const PRICE_PER_DAY = 1200;

  useEffect(() => {
    fetchAvailablePeriods();
  }, []);

  const fetchAvailablePeriods = async () => {
    try {
      const response = await axios.get(`${API}/weekend-date-periods?active_only=true`);
      setAvailablePeriods(response.data);
    } catch (error) {
      console.error('Error fetching periods:', error);
    } finally {
      setLoadingPeriods(false);
    }
  };

  const formatPeriodDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString(i18n.language === 'da' ? 'da-DK' : i18n.language === 'de' ? 'de-DE' : 'en-GB', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const handleSubmit = async () => {
    if (!user) {
      toast.error(t('You must be logged in'));
      navigate('/login');
      return;
    }

    if (!formData.stable_name || !formData.stable_address) {
      toast.error(t('Please fill in name/stable and address'));
      return;
    }

    if (!formData.selected_date_period) {
      toast.error(t('Please select a date period'));
      return;
    }

    // Day preference is required for 2 or 3 day bookings
    if (numDays < 4 && !formData.day_preference) {
      toast.error(t('Please select which days'));
      return;
    }

    setLoading(true);
    try {
      const courseData = {
        instructor_id: INSTRUCTOR_ID,
        instructor_name: 'Julie Christiansen',
        num_days: numDays,
        client_name: user.name,
        client_email: user.email,
        client_phone: user.phone || '',
        stable_name: formData.stable_name,
        stable_address: formData.stable_address,
        selected_date_period: formData.selected_date_period,
        day_preference: formData.day_preference,
        alternative_date_period: formData.alternative_date_period || null,
        alternative_day_preference: formData.alternative_date_period ? formData.alternative_day_preference : null,
        course_type: formData.course_type,
        additional_notes: formData.additional_notes,
        language: i18n.language,
        booking_type: 'weekend-external'
      };

      await axios.post(`${API}/weekend-courses`, courseData);

      toast.success(t('Request sent successfully'));
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error(error.response?.data?.detail || t('Could not send request'));
    } finally {
      setLoading(false);
    }
  };

  const totalPrice = numDays * PRICE_PER_DAY;

  // Check if a specific day range is available (not overlapping with booked ranges)
  const isDayRangeAvailable = (periodId, preference) => {
    const period = availablePeriods.find(p => p.id === periodId);
    if (!period || !period.booked_ranges || period.booked_ranges.length === 0) {
      return true; // No bookings, all available
    }
    
    const periodStart = new Date(period.start_date + 'T00:00:00');
    const periodEnd = new Date(period.end_date + 'T00:00:00');
    
    let rangeStart, rangeEnd;
    if (preference === 'first') {
      rangeStart = new Date(periodStart);
      rangeEnd = new Date(periodStart);
      rangeEnd.setDate(rangeStart.getDate() + numDays - 1);
    } else {
      rangeEnd = new Date(periodEnd);
      rangeStart = new Date(periodEnd);
      rangeStart.setDate(rangeEnd.getDate() - numDays + 1);
    }
    
    // Check if this range overlaps with any booked range
    for (const booked of period.booked_ranges) {
      const bookedStart = new Date(booked.start + 'T00:00:00');
      const bookedEnd = new Date(booked.end + 'T00:00:00');
      
      // Check for overlap
      if (rangeStart <= bookedEnd && rangeEnd >= bookedStart) {
        return false; // Overlap found, not available
      }
    }
    
    return true;
  };

  const getDayPreferenceOptions = (periodId = null) => {
    const options = [];
    const checkPeriod = periodId || formData.selected_date_period;
    
    if (numDays === 2) {
      if (!checkPeriod || isDayRangeAvailable(checkPeriod, 'first')) {
        options.push({ value: 'first', label: t('First 2 days') });
      }
      if (!checkPeriod || isDayRangeAvailable(checkPeriod, 'last')) {
        options.push({ value: 'last', label: t('Last 2 days') });
      }
    } else if (numDays === 3) {
      if (!checkPeriod || isDayRangeAvailable(checkPeriod, 'first')) {
        options.push({ value: 'first', label: t('First 3 days') });
      }
      if (!checkPeriod || isDayRangeAvailable(checkPeriod, 'last')) {
        options.push({ value: 'last', label: t('Last 3 days') });
      }
    } else if (numDays === 4) {
      if (!checkPeriod || isDayRangeAvailable(checkPeriod, 'first')) {
        options.push({ value: 'first', label: t('All 4 days') });
      }
    }
    return options;
  };

  return (
    <div>
      <Navbar />
      <div className="booking-page">
        <button onClick={() => navigate('/')} className="back-arrow-btn">
          <ArrowLeft size={24} />
        </button>
        
        <div className="booking-header">
          <h1 className="booking-title">{t('External Weekend Courses')}</h1>
          <p style={{ color: '#6b6b6b', fontSize: '1.1rem' }}>{t('Host Julie at your stable')}</p>
        </div>

        <div style={{ maxWidth: '750px', margin: '0 auto' }}>
          {/* Info text - subtle */}
          <p style={{
            color: '#7c6f5e',
            fontSize: '0.95rem',
            marginBottom: '2rem',
            lineHeight: 1.6,
            fontStyle: 'italic'
          }}>
            {t('Date flexibility description')}
          </p>

          {/* About Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.4rem', marginBottom: '1rem', color: '#7c6f5e' }}>{t('About Weekend Courses')}</h2>
            <div style={{ color: '#6b6b6b', lineHeight: '1.7' }}>
              <p>{t('Weekend course intro')}</p>
              <br />
              <p>
                <strong>{t('Course Details')}</strong><br />
                • {t('Duration')}: {t('Min 2 max 4 days')}<br />
                • {t('Maximum')}: 10 {t('riders')}<br />
                • {t('Price per day')}: €{PRICE_PER_DAY} ({t('includes travel')})<br />
                • {t('Extra costs')}: {t('Accommodation and meals')}
              </p>
            </div>
          </div>

          {/* Booking Form */}
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <h3 style={{ fontSize: '1.3rem', marginBottom: '1.5rem', color: '#7c6f5e' }}>
              {t('Request Course')}
            </h3>

            {/* Antal Dage */}
            <div className="form-group">
              <label className="form-label">{t('Number of Days')}</label>
              <div className="payment-methods">
                {[2, 3, 4].map((days) => (
                  <div
                    key={days}
                    className={`payment-method ${numDays === days ? 'selected' : ''}`}
                    onClick={() => {
                      setNumDays(days);
                      setFormData({ ...formData, day_preference: 'first' });
                    }}
                    data-testid={`days-${days}`}
                  >
                    {days} {t('Days')}<br />
                    <span style={{ fontSize: '1.125rem', fontWeight: 600 }}>€{(days * PRICE_PER_DAY).toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: '1rem', color: '#8b4857', fontWeight: 500 }}>
                <span style={{ fontSize: '1.25rem', fontWeight: 600, display: 'block' }}>
                  Total: €{totalPrice.toLocaleString()}
                </span>
                <span style={{ fontSize: '0.875rem' }}>{t('Plus accommodation')}</span>
              </p>
            </div>

            {/* Navn/Stald/Klub - Required */}
            <div className="form-group">
              <label className="form-label">{t('Name/Stable/Club')} <span style={{ color: '#c62828' }}>*</span></label>
              <input
                type="text"
                className="form-input"
                value={formData.stable_name}
                onChange={(e) => setFormData({ ...formData, stable_name: e.target.value })}
                placeholder={t('Your name, stable or club')}
                data-testid="stable-name-input"
                required
              />
            </div>

            {/* Adresse - Required */}
            <div className="form-group">
              <label className="form-label">{t('Address')} <span style={{ color: '#c62828' }}>*</span></label>
              <input
                type="text"
                className="form-input"
                value={formData.stable_address}
                onChange={(e) => setFormData({ ...formData, stable_address: e.target.value })}
                placeholder={t('Course location address')}
                data-testid="address-input"
                required
              />
            </div>

            {/* Dato Valg */}
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={18} color="#7c6f5e" />
                {t('Preferred Date')} <span style={{ color: '#c62828' }}>*</span>
              </label>
              
              {loadingPeriods ? (
                <p style={{ color: '#888' }}>{t('Loading dates')}...</p>
              ) : availablePeriods.length === 0 ? (
                <div style={{ 
                  background: '#fff3e0', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  color: '#e65100'
                }}>
                  {t('No dates available')}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {availablePeriods.map((period) => {
                    const canBook = (period.remaining_days || period.total_days || 4) >= numDays;
                    const isSelected = formData.selected_date_period === period.id;
                    const dayOptions = getDayPreferenceOptions(period.id);
                    
                    return (
                      <div key={period.id}>
                        {/* Periode kort */}
                        <div
                          onClick={() => canBook && setFormData({ 
                            ...formData, 
                            selected_date_period: period.id,
                            day_preference: '' // Reset day preference when selecting new period
                          })}
                          data-testid={`period-${period.id}`}
                          style={{
                            padding: '1rem 1.25rem',
                            border: isSelected 
                              ? '2px solid #7c6f5e' 
                              : '1px solid #e0e0e0',
                            borderRadius: isSelected && numDays < 4 ? '10px 10px 0 0' : '10px',
                            cursor: canBook ? 'pointer' : 'not-allowed',
                            background: !canBook 
                              ? '#f5f5f5'
                              : isSelected 
                                ? '#f5f0eb' 
                                : 'white',
                            opacity: canBook ? 1 : 0.6,
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, color: '#333', marginBottom: '0.25rem' }}>
                              {period.description || `${formatPeriodDate(period.start_date)} - ${formatPeriodDate(period.end_date)}`}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#666' }}>
                              {formatPeriodDate(period.start_date)} → {formatPeriodDate(period.end_date)}
                              {period.remaining_days !== undefined && period.remaining_days < period.total_days && (
                                <span style={{ 
                                  marginLeft: '0.75rem',
                                  color: period.remaining_days >= numDays ? '#2e7d32' : '#c62828',
                                  fontWeight: 500
                                }}>
                                  • {period.remaining_days} {t('days available')}
                                </span>
                              )}
                            </div>
                            {!canBook && (
                              <div style={{ fontSize: '0.8rem', color: '#c62828', marginTop: '0.25rem' }}>
                                {t('Not enough days available')}
                              </div>
                            )}
                          </div>
                          {isSelected && canBook && (
                            <CheckCircle size={24} color="#7c6f5e" />
                          )}
                        </div>
                        
                        {/* Dag-valg - vises direkte under den valgte periode */}
                        {isSelected && canBook && numDays < 4 && (
                          <div style={{
                            background: '#f8f6f3',
                            padding: '1rem 1.25rem',
                            borderRadius: '0 0 10px 10px',
                            borderLeft: '2px solid #7c6f5e',
                            borderRight: '2px solid #7c6f5e',
                            borderBottom: '2px solid #7c6f5e'
                          }}>
                            <label style={{ 
                              display: 'block', 
                              marginBottom: '0.75rem', 
                              fontSize: '0.9rem', 
                              fontWeight: 600,
                              color: '#7c6f5e'
                            }}>
                              {t('Which days')} <span style={{ color: '#c62828' }}>*</span>
                            </label>
                            
                            {dayOptions.length === 0 ? (
                              <div style={{ 
                                padding: '0.75rem', 
                                background: '#ffebee', 
                                borderRadius: '6px',
                                color: '#c62828',
                                fontSize: '0.85rem'
                              }}>
                                {t('No day options available for this period')}
                              </div>
                            ) : dayOptions.length === 1 ? (
                              <div 
                                onClick={() => setFormData({ ...formData, day_preference: dayOptions[0].value })}
                                style={{ 
                                  padding: '0.75rem 1rem', 
                                  background: formData.day_preference === dayOptions[0].value ? '#c8e6c9' : '#e8f5e9', 
                                  borderRadius: '8px',
                                  color: '#2e7d32',
                                  fontSize: '0.9rem',
                                  cursor: 'pointer',
                                  border: formData.day_preference === dayOptions[0].value ? '2px solid #4caf50' : '1px solid #c8e6c9'
                                }}
                              >
                                {t('Only available')}: <strong>{dayOptions[0].label}</strong>
                                {formData.day_preference === dayOptions[0].value && ' ✓'}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                {dayOptions.map((option) => (
                                  <div
                                    key={option.value}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFormData({ ...formData, day_preference: option.value });
                                    }}
                                    style={{
                                      padding: '0.6rem 1.25rem',
                                      border: formData.day_preference === option.value 
                                        ? '2px solid #7c6f5e' 
                                        : '1px solid #d0d0d0',
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                      background: formData.day_preference === option.value 
                                        ? 'white' 
                                        : '#fafafa',
                                      fontWeight: formData.day_preference === option.value ? 600 : 400,
                                      fontSize: '0.9rem'
                                    }}
                                  >
                                    {option.label}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Alternativ Dato - Valgfrit */}
            <div className="form-group">
              <label className="form-label">{t('Alternative Date')} <span style={{ color: '#888', fontWeight: 400 }}>({t('optional')})</span></label>
              <select
                className="form-select"
                value={formData.alternative_date_period}
                onChange={(e) => setFormData({ ...formData, alternative_date_period: e.target.value, alternative_day_preference: 'first' })}
                data-testid="alternative-date-select"
              >
                <option value="">{t('No alternative')}</option>
                {availablePeriods
                  .filter(p => p.id !== formData.selected_date_period)
                  .map((period) => (
                    <option key={period.id} value={period.id}>
                      {period.description || `${formatPeriodDate(period.start_date)} - ${formatPeriodDate(period.end_date)}`}
                    </option>
                  ))}
              </select>
            </div>

            {/* Dag præference for alternativ dato */}
            {formData.alternative_date_period && numDays < 4 && (
              <div className="form-group">
                <label className="form-label">{t('Which days for alternative')}</label>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  {getDayPreferenceOptions().map((option) => (
                    <div
                      key={`alt-${option.value}`}
                      onClick={() => setFormData({ ...formData, alternative_day_preference: option.value })}
                      style={{
                        padding: '0.75rem 1.5rem',
                        border: formData.alternative_day_preference === option.value 
                          ? '2px solid #7c6f5e' 
                          : '1px solid #e0e0e0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: formData.alternative_day_preference === option.value 
                          ? '#f5f0eb' 
                          : 'white',
                        fontWeight: formData.alternative_day_preference === option.value ? 600 : 400
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Kursustype med flot layout */}
            <div className="form-group">
              <label className="form-label">{t('Course Type')}</label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div
                  onClick={() => setFormData({ ...formData, course_type: 'single' })}
                  data-testid="course-type-single"
                  style={{
                    flex: '1 1 200px',
                    padding: '1.25rem',
                    border: formData.course_type === 'single' 
                      ? '2px solid #7c6f5e' 
                      : '1px solid #e0e0e0',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    background: formData.course_type === 'single' 
                      ? '#f5f0eb' 
                      : 'white',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📅</div>
                  <div style={{ fontWeight: 600, color: '#333' }}>{t('Single Course')}</div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>{t('One-time event')}</div>
                </div>
                <div
                  onClick={() => setFormData({ ...formData, course_type: 'series' })}
                  data-testid="course-type-series"
                  style={{
                    flex: '1 1 200px',
                    padding: '1.25rem',
                    border: formData.course_type === 'series' 
                      ? '2px solid #7c6f5e' 
                      : '1px solid #e0e0e0',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    background: formData.course_type === 'series' 
                      ? '#f5f0eb' 
                      : 'white',
                    textAlign: 'center'
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>🗓️</div>
                  <div style={{ fontWeight: 600, color: '#333' }}>{t('Course Series')}</div>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>{t('Multiple throughout year')}</div>
                </div>
              </div>
            </div>

            {/* Yderligere info - fuld bredde */}
            <div className="form-group" style={{ marginTop: '1.5rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #f8f6f3 0%, #f0ece6 100%)',
                padding: '1.5rem',
                borderRadius: '12px',
                border: '1px solid #e5e0d8',
                width: '100%'
              }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: '#7c6f5e',
                  marginBottom: '0.75rem'
                }}>
                  <span style={{ fontSize: '1.25rem' }}>💬</span>
                  {t('Additional Information')} 
                  <span style={{ color: '#999', fontWeight: 400, fontSize: '0.85rem' }}>({t('optional')})</span>
                </label>
                <p style={{ 
                  fontSize: '0.85rem', 
                  color: '#888', 
                  marginBottom: '1rem',
                  lineHeight: 1.5
                }}>
                  {t('Additional info description')}
                </p>
                <textarea
                  className="form-textarea"
                  value={formData.additional_notes}
                  onChange={(e) => setFormData({ ...formData, additional_notes: e.target.value })}
                  rows="4"
                  placeholder={t('Additional info placeholder')}
                  data-testid="additional-notes"
                  style={{
                    resize: 'vertical',
                    minHeight: '100px',
                    width: '100%',
                    boxSizing: 'border-box',
                    background: 'white',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '1rem',
                    fontSize: '0.95rem',
                    lineHeight: 1.6
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              className="cta-button"
              onClick={handleSubmit}
              disabled={loading || !formData.stable_name || !formData.stable_address || !formData.selected_date_period}
              data-testid="submit-button"
              style={{ width: '100%', marginTop: '1.5rem' }}
            >
              {loading ? t('Sending') + '...' : t('Send Request')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeekendKursExternalPage;
